import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/api/endpoints/reports';
import { useFleetStore } from '@/stores/fleet';
import { formatDateTime } from '@/lib/utils';
import { formatAddress } from '@/lib/address';
import {
  exportCsv,
  formatDuration,
  ReportFilters,
  ReportShell,
  ReportTable,
  ResultBar,
  useReportFilters,
} from './_shared';

export function StopsReportPage() {
  const devices = useFleetStore((s) => s.devices);
  const { deviceId, setDeviceId, preset, setPreset, executed, generate } = useReportFilters('multi');

  const query = useQuery({
    queryKey: ['report', 'stops', executed],
    queryFn: () =>
      reportsApi.stops({
        deviceId: executed!.ids,
        from: executed!.range.from,
        to: executed!.range.to,
      }),
    enabled: executed !== null,
  });

  const rows = useMemo(
    () =>
      [...(query.data ?? [])].sort(
        (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
      ),
    [query.data],
  );

  const handleExport = () => {
    if (!executed) return;
    exportCsv(
      `paradas_${executed.range.from.slice(0, 10)}_${executed.range.to.slice(0, 10)}.csv`,
      ['Embarcação', 'Início', 'Fim', 'Duração', 'Endereço'],
      rows.map((r) => [
        r.deviceName ?? devices.get(r.deviceId)?.name ?? '',
        formatDateTime(r.startTime),
        formatDateTime(r.endTime),
        formatDuration(r.duration),
        formatAddress(r.address),
      ]),
    );
  };

  return (
    <ReportShell title="Paradas" description="Períodos em que cada embarcação ficou parada.">
      <ReportFilters
        deviceMode="multi"
        deviceId={deviceId}
        onDeviceChange={setDeviceId}
        preset={preset}
        onPresetChange={setPreset}
        onGenerate={generate}
        generating={query.isFetching}
      />
      {executed && (
        <ResultBar
          loading={query.isLoading}
          count={rows.length}
          unit="paradas"
          onExport={handleExport}
        />
      )}
      <ReportTable
        notRun={executed === null}
        loading={query.isLoading}
        error={query.error}
        empty={rows.length === 0}
      >
        {/* rolagem horizontal: tabela larga nao pode empurrar a pagina inteira no celular */}
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-semibold">Embarcação</th>
              <th className="px-4 py-2 font-semibold">Início</th>
              <th className="px-4 py-2 font-semibold">Fim</th>
              <th className="px-4 py-2 font-semibold">Duração</th>
              <th className="px-4 py-2 font-semibold">Endereço</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.deviceId}-${i}`} className="border-b border-border last:border-0 hover:bg-accent/40">
                <td className="px-4 py-2 font-semibold">{r.deviceName}</td>
                <td className="whitespace-nowrap px-4 py-2 text-xs">{formatDateTime(r.startTime)}</td>
                <td className="whitespace-nowrap px-4 py-2 text-xs">{formatDateTime(r.endTime)}</td>
                <td className="px-4 py-2">{formatDuration(r.duration)}</td>
                <td className="px-4 py-2 text-xs">{formatAddress(r.address) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </ReportTable>
    </ReportShell>
  );
}
